import { useMemo } from 'react';
import { css } from '@linaria/core';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Link, TextTitle1, TextTitle3, TextTitle4 } from '@cbhq/cds-web/typography';
import { ProgressBar } from '@cbhq/cds-web/visualizations/ProgressBar';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { createConfetti } from ':cloud/widgets/buildQuest/confetti';
import { LEVELS } from ':cloud/widgets/buildQuest/constants';

type ProgressBarSectionProps = {
  playerLevel: number;
  currentLevelColor: string;
  displayedPoints: number;
  width?: string;
  onPressPrizes: () => void;
};

export function ProgressBarSection({
  playerLevel,
  currentLevelColor,
  displayedPoints,
  width,
  onPressPrizes,
}: ProgressBarSectionProps) {
  // Set CSS variable for progress bar color since linaria doesn't support dynamic styling
  document.documentElement.style.setProperty('--progress-bar-color', currentLevelColor);

  const progressBar = css`
    [data-testid='cds-progress-bar-inner-bar'] {
      background-color: var(--progress-bar-color) !important;
      height: 8px !important;
    }
    [data-testid='cds-progress-bar-inner-bar-container'] {
      height: 8px !important;
    }
  `;

  const { isPhone } = useSimpleBreakpoints();

  const nextLevel = LEVELS[playerLevel + 1];
  const progressBarValue = useMemo(() => {
    return nextLevel ? displayedPoints / nextLevel.points : 0;
  }, [displayedPoints, nextLevel]);

  const pointsRemainingToNextLevel = useMemo(() => {
    return nextLevel ? nextLevel.points - displayedPoints : 0;
  }, [displayedPoints, nextLevel]);

  const toNextLevelCopy = useMemo(() => {
    if (nextLevel?.level < 4) {
      return `+${pointsRemainingToNextLevel} EXP to level ${nextLevel?.level}`;
    }
    return `+${pointsRemainingToNextLevel} EXP to complete Quest`;
  }, [pointsRemainingToNextLevel, nextLevel]);

  if (playerLevel === 4) {
    createConfetti();
    return (
      <VStack justifyContent="center" alignItems="center" width={width}>
        <TextTitle1 as="span" color="foreground">
          Congratulations!{!isPhone && ' You completed CDP Quest'}
        </TextTitle1>
        {isPhone && (
          <Box spacingBottom={1}>
            <TextTitle1 as="span" color="foreground">
              You completed CDP Quest
            </TextTitle1>
          </Box>
        )}
        <Link onPress={onPressPrizes}>
          <TextTitle3 as="p" dangerouslySetColor={currentLevelColor}>
            Claim your prizes →
          </TextTitle3>
        </Link>
      </VStack>
    );
  }

  return (
    <VStack className={progressBar} justifyContent="center" gap={2} width={width}>
      <ProgressBar weight="heavy" progress={progressBarValue} />
      <HStack justifyContent="space-between">
        <TextTitle4 as="h4" dangerouslySetColor={currentLevelColor}>
          +{displayedPoints} EXP
        </TextTitle4>
        <TextTitle4 as="h4" color="foregroundMuted">
          {toNextLevelCopy}
        </TextTitle4>
      </HStack>
    </VStack>
  );
}
